import { BASE_INTERVIEWER_PROMPT, PROMPT_VARIABLES, INTERVIEW_TYPE_CONFIGS } from '../config/interviewer-agent-prompt';
import { InterviewSettings } from '../types';

/**
 * Default interview type if not specified
 */
const DEFAULT_INTERVIEW_TYPE = 'coding';

/**
 * Loads the base prompt template
 */
export function getBasePromptTemplate(): string {
    return BASE_INTERVIEWER_PROMPT;
}

/**
 * Replaces template variables with actual values
 */
function replaceVariables(template: string, values: Record<string, string>): string {
    let result = template;
    for (const [key, value] of Object.entries(values)) {
        const placeholder = `{${key}}`;
        result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value || '');
    }
    return result;
}

/**
 * Formats the allowed languages array into a readable string
 */
function formatAllowedLanguages(languages: string[]): string {
    if (!languages || languages.length === 0) {
        return 'Any programming language';
    }
    if (languages.length === 1) {
        return languages[0];
    }
    if (languages.length === 2) {
        return `${languages[0]} or ${languages[1]}`;
    }
    const lastLang = languages[languages.length - 1];
    const otherLangs = languages.slice(0, -1).join(', ');
    return `${otherLangs}, or ${lastLang}`;
}

/**
 * Formats a date string into a readable format
 */
function formatDate(dateString: string): string {
    if (!dateString) {
        return 'Not specified';
    }
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
}

/**
 * Determines interview type based on interview settings
 * Can be extended to support explicit interview type field in the future
 */
function determineInterviewType(interview: InterviewSettings): string {
    // For now, default to coding. In the future, this could be a field in InterviewSettings
    // Check if description or title contains keywords
    const description = (interview.description || '').toLowerCase();
    const title = (interview.title || '').toLowerCase();

    if (description.includes('system design') || title.includes('system design')) {
        return 'system-design';
    }
    if (description.includes('behavioral') || title.includes('behavioral')) {
        return 'behavioral';
    }
    if (description.includes('comprehensive') || title.includes('comprehensive')) {
        return 'comprehensive';
    }

    return DEFAULT_INTERVIEW_TYPE;
}

/**
 * Builds a basic candidate context string from available interview fields
 * Used as fallback when no full research data is available
 */
function buildCandidateContext(interview: InterviewSettings): string {
    const parts: string[] = [];

    if (interview.candidateName) {
        parts.push(`Candidate Name: ${interview.candidateName}`);
    }
    if (interview.candidateLinkedIn) {
        parts.push(`LinkedIn Profile: ${interview.candidateLinkedIn}`);
    }
    if (interview.candidateDocuments && interview.candidateDocuments.length > 0) {
        const docNames = interview.candidateDocuments.map(d => d.name).join(', ');
        parts.push(`Attached Documents: ${docNames}`);
    }

    return parts.length > 0
        ? parts.join('\n')
        : 'No candidate background information available.';
}

/**
 * Generates the final system prompt for a given interview
 * Merges interview settings into the base template
 */
export function generateInterviewPrompt(interview: InterviewSettings, customPrompt?: string): string {
    // If a custom prompt is provided, use it directly (but still replace variables)
    const template = customPrompt || BASE_INTERVIEWER_PROMPT;

    const interviewType = determineInterviewType(interview);
    const typeConfig = INTERVIEW_TYPE_CONFIGS[interviewType as keyof typeof INTERVIEW_TYPE_CONFIGS] || INTERVIEW_TYPE_CONFIGS.coding;

    const values: Record<string, string> = {
        interviewTitle: interview.title || 'Technical Interview',
        interviewType: typeConfig.name,
        difficulty: interview.difficulty || 'Medium',
        timeLimit: interview.timeLimit ? `${interview.timeLimit}` : '60',
        candidateContext: interview.candidateResearchData || buildCandidateContext(interview),
        interviewInstructions: interview.instructions || 'Please solve the coding problem below. Explain your approach and think through edge cases.',
        problemContent: interview.content || 'No problem statement provided.',
        allowedLanguages: formatAllowedLanguages(interview.allowedLanguages || []),
        status: interview.status || 'draft',
        createdAt: formatDate(interview.createdAt || '')
    };

    return replaceVariables(template, values);
}

/**
 * Validates that a custom prompt contains necessary elements
 */
export function validateCustomPrompt(prompt: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!prompt || prompt.trim().length === 0) {
        errors.push('Prompt cannot be empty');
    }

    if (prompt.length < 100) {
        errors.push('Prompt seems too short. Consider adding more detail.');
    }

    // Check for key elements (optional validation)
    const lowerPrompt = prompt.toLowerCase();
    if (!lowerPrompt.includes('interview') && !lowerPrompt.includes('candidate')) {
        errors.push('Prompt should mention the interview or candidate context');
    }

    return {
        valid: errors.length === 0,
        errors
    };
}

/**
 * Gets a preview of the prompt with interview settings applied
 */
export function getPromptPreview(interview: InterviewSettings, customPrompt?: string): string {
    const prompt = generateInterviewPrompt(interview, customPrompt);
    // Return first 500 characters as preview
    return prompt.length > 500 ? prompt.substring(0, 500) + '...' : prompt;
}




















